/**
 * Step 7.5: SOA Review - EMZI Care CI v6.3
 * Review all SOA Drill answers with Brain Points scores and improvement feedback
 * 
 * Design Standards:
 * - EMZI Blue (#0072E5) for primary elements and headers
 * - Flat, clean design with consistent spacing (12px compact standard)
 * - Rounded corners (8px standard, 12px for cards)
 * - Subtle shadows for depth
 * - Typography: 14px body, 18px section titles, 600-700 font weights
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  EMZI_COLORS, 
  EMZI_SHADOWS, 
  EMZI_SPACING, 
  EMZI_TYPOGRAPHY,
  EMZI_RADIUS,
  EMZI_BUTTON_STYLES 
} from '../../../styles/emziCI';

const Step7_5_SOAReview = ({ 
  sessionId, 
  token, 
  sessionData, 
  onComplete, 
  onBack, 
  onEditQuestion,
  setError 
}) => {
  const { t, language } = useLanguage();
  const [improvementFeedback, setImprovementFeedback] = useState(null);
  const [totalBrainPoints, setTotalBrainPoints] = useState(0);
  
  // Get SOA data
  const soaPlan = sessionData?.soaPlan;
  const blueprint = soaPlan?.blueprint || [];
  const answers = sessionData?.soa_answers || {};
  const narrative = sessionData?.narrative || soaPlan?.narrative;
  
  // Calculate scores and brain points
  useEffect(() => {
    const calculateMetrics = () => {
      let totalScore = 0;
      let questionCount = 0;
      let brainPoints = 0;
      let aiAssistedCount = 0;
      const improvements = [];
      
      Object.keys(answers).forEach(qIndex => {
        const answer = answers[qIndex];
        if (answer?.bestScore !== null && answer?.bestScore !== undefined) {
          totalScore += answer.bestScore;
          questionCount++;
          
          // Calculate Brain Points: Score * 10 (max 100 per question)
          let points = Math.round(answer.bestScore * 10);
          
          // Apply 30% penalty if AI Rescue was used
          if (answer.aiRescueUsed) {
            points = Math.round(points * 0.7); // 30% reduction
            aiAssistedCount++;
          }
          
          brainPoints += points;
          
          // Identify improvement areas (score < 8)
          if (answer.bestScore < 8.0) {
            const question = blueprint[qIndex];
            improvements.push({
              qIndex: parseInt(qIndex),
              question: question?.q || '',
              score: answer.bestScore,
              focus: question?.focus || [],
              suggestion: generateSuggestion(answer.bestScore, question)
            });
          }
        }
      });
      
      const avgScore = questionCount > 0 ? (totalScore / questionCount) : 0;
      
      setTotalBrainPoints(brainPoints);
      setImprovementFeedback({
        avgScore,
        questionCount,
        totalPossiblePoints: 500, // 5 questions × 100 points max
        aiAssistedCount,
        improvements,
        overallFeedback: generateOverallFeedback(avgScore, improvements.length)
      });
    };
    
    calculateMetrics();
  }, [answers, blueprint]);
  
  // Generate suggestion based on score and question focus
  const generateSuggestion = (score, question) => {
    if (score >= 7.0) {
      return language === 'bm' 
        ? 'Hampir sempurna! Cuba tambah lebih detail spesifik.'
        : 'Almost perfect! Try adding more specific details.';
    } else if (score >= 5.0) {
      return language === 'bm'
        ? 'Baik, tetapi fokus lebih pada objektif dan audience.'
        : 'Good, but focus more on objectives and audience.';
    } else {
      return language === 'bm'
        ? 'Perlu penambahbaikan. Cuba lihat semula narrative dan jawab dengan lebih mendalam.'
        : 'Needs improvement. Review the narrative and answer more deeply.';
    }
  };
  
  // Generate overall feedback
  const generateOverallFeedback = (avgScore, improvementsCount) => {
    if (avgScore >= 9.0) {
      return {
        level: 'excellent',
        title: language === 'bm' ? '🌟 Cemerlang!' : '🌟 Excellent!',
        message: language === 'bm'
          ? 'Jawapan anda sangat kukuh dan siap untuk script generation!'
          : 'Your answers are very strong and ready for script generation!'
      };
    } else if (avgScore >= 8.0) {
      return {
        level: 'good',
        title: language === 'bm' ? '✅ Bagus!' : '✅ Great!',
        message: language === 'bm'
          ? 'Jawapan anda kukuh. Proceed dengan keyakinan!'
          : 'Your answers are strong. Proceed with confidence!'
      };
    } else if (avgScore >= 6.5) {
      return {
        level: 'ok',
        title: language === 'bm' ? '⚠️ OK, Boleh Lebih Baik' : '⚠️ OK, Can Improve',
        message: language === 'bm'
          ? `${improvementsCount} soalan perlu penambahbaikan. Edit untuk skor lebih tinggi?`
          : `${improvementsCount} questions need improvement. Edit for higher scores?`
      };
    } else {
      return {
        level: 'weak',
        title: language === 'bm' ? '❌ Perlu Penambahbaikan' : '❌ Needs Improvement',
        message: language === 'bm'
          ? 'Beberapa jawapan terlalu ringkas. Cuba lagi untuk Brain Points lebih tinggi!'
          : 'Some answers are too brief. Try again for higher Brain Points!'
      };
    }
  };
  
  // Get score color
  const getScoreColor = (score) => {
    if (score >= 8.0) return EMZI_COLORS.success;
    if (score >= 6.5) return EMZI_COLORS.warning;
    return EMZI_COLORS.error;
  };
  
  // Get brain points tier
  const getBrainPointsTier = (points, maxPoints) => {
    const percentage = (points / maxPoints) * 100;
    if (percentage >= 90) return { tier: 'Master', emoji: '👑', color: '#FFD700' };
    if (percentage >= 80) return { tier: 'Expert', emoji: '🏆', color: '#16A34A' };
    if (percentage >= 70) return { tier: 'Advanced', emoji: '⭐', color: '#0EA5E9' };
    if (percentage >= 60) return { tier: 'Intermediate', emoji: '📈', color: '#F59E0B' };
    return { tier: 'Beginner', emoji: '🌱', color: '#999999' };
  };
  
  if (!improvementFeedback) {
    return (
      <div style={{ 
        padding: EMZI_SPACING.lg, 
        textAlign: 'center',
        color: EMZI_COLORS.textSecondary 
      }}>
        {language === 'bm' ? 'Mengira skor...' : 'Calculating scores...'}
      </div>
    );
  }
  
  const tier = getBrainPointsTier(totalBrainPoints, improvementFeedback.totalPossiblePoints);
  const percentage = Math.round((totalBrainPoints / improvementFeedback.totalPossiblePoints) * 100);
  
  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '12px',
      paddingBottom: `calc(20px + env(safe-area-inset-bottom, 20px))` // Normal padding for mobile safe area
    }}>
      {/* Header - Compact */}
      <div style={{
        background: EMZI_COLORS.primary,
        color: EMZI_COLORS.textWhite,
        padding: '12px', // Reduced from EMZI_SPACING.lg (16px)
        borderRadius: '10px', // Reduced from EMZI_RADIUS.lg (12px)
        marginBottom: '12px', // Reduced from EMZI_SPACING.lg (16px)
        boxShadow: EMZI_SHADOWS.header
      }}>
        <h2 style={{ 
          ...EMZI_TYPOGRAPHY.h2,
          fontSize: '18px', // Reduced from 20px
          fontWeight: 700, 
          marginBottom: '6px', // Reduced from EMZI_SPACING.sm (8px)
          display: 'flex',
          alignItems: 'center',
          gap: '6px', // Reduced from EMZI_SPACING.sm (8px)
          color: EMZI_COLORS.textWhite
        }}>
          🧠 {language === 'bm' ? 'Ringkasan Brain Points' : 'Brain Points Summary'}
        </h2>
        <p style={{ 
          ...EMZI_TYPOGRAPHY.body,
          fontSize: '13px', // Reduced from 14px
          opacity: 0.95, 
          margin: 0,
          color: EMZI_COLORS.textWhite
        }}>
          {language === 'bm' 
            ? 'Refleksi dan pembelajaran dari SOA Drill anda'
            : 'Reflection and learning insights from your SOA Drill'}
        </p>
      </div>
      
      {/* Brain Points Summary Card - Compact */}
      <div style={{
        background: EMZI_COLORS.background,
        border: `2px solid ${tier.color}`,
        borderRadius: '10px', // Reduced from EMZI_RADIUS.lg (12px)
        padding: '12px', // Reduced from EMZI_SPACING.lg (16px)
        marginBottom: '12px', // Reduced from EMZI_SPACING.lg (16px)
        boxShadow: EMZI_SHADOWS.card,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Tier Badge - Compact */}
        <div style={{
          position: 'absolute',
          top: '8px', // Reduced from EMZI_SPACING.md (12px)
          right: '8px', // Reduced from EMZI_SPACING.md (12px)
          background: tier.color,
          color: EMZI_COLORS.textWhite,
          padding: '4px 10px', // Reduced from 6px 12px
          borderRadius: EMZI_RADIUS.full,
          fontSize: '11px', // Reduced from 12px
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: '3px', // Reduced from 4px
          boxShadow: EMZI_SHADOWS.sm
        }}>
          {tier.emoji} {tier.tier}
        </div>
        
        {/* Brain Points Display - Compact */}
        <div style={{ textAlign: 'center', marginTop: '8px' }}> {/* Reduced from EMZI_SPACING.md (12px) */}
          <div style={{ 
            fontSize: '13px', // Reduced from 14px
            color: EMZI_COLORS.textSecondary,
            marginBottom: '4px', // Reduced from EMZI_SPACING.xs (6px)
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontWeight: 600
          }}>
            {language === 'bm' ? 'Brain Points' : 'Brain Points'}
          </div>
          <div style={{ 
            fontSize: '40px', // Reduced from 48px
            fontWeight: 700,
            color: tier.color,
            lineHeight: 1,
            marginBottom: '4px' // Reduced from EMZI_SPACING.xs (6px)
          }}>
            {totalBrainPoints}
            <span style={{ 
              fontSize: '20px', // Reduced from 24px
              color: EMZI_COLORS.textMuted,
              fontWeight: 400 
            }}>
              /{improvementFeedback.totalPossiblePoints}
            </span>
          </div>
          <div style={{
            fontSize: '14px', // Reduced from 16px
            color: EMZI_COLORS.textSecondary,
            marginBottom: '10px' // Reduced from EMZI_SPACING.md (12px)
          }}>
            {percentage}% {language === 'bm' ? 'Kemahiran' : 'Mastery'}
          </div>
          
          {/* Progress Bar - Compact */}
          <div style={{
            width: '100%',
            height: '10px', // Reduced from 12px
            background: EMZI_COLORS.surface,
            borderRadius: '4px', // Reduced from EMZI_RADIUS.sm (6px)
            overflow: 'hidden',
            marginBottom: '10px', // Reduced from EMZI_SPACING.md (12px)
            border: `1px solid ${EMZI_COLORS.borderLight}`
          }}>
            <div style={{
              width: `${percentage}%`,
              height: '100%',
              background: tier.color,
              transition: 'width 0.5s ease',
              boxShadow: `0 0 8px ${tier.color}40`
            }} />
          </div>
          
          {/* Overall Feedback - Compact */}
          <div style={{
            padding: '10px', // Reduced from EMZI_SPACING.md (12px)
            background: EMZI_COLORS.surface,
            borderRadius: '6px', // Reduced from EMZI_RADIUS.md (8px)
            border: `1px solid ${EMZI_COLORS.border}`,
            boxShadow: EMZI_SHADOWS.sm
          }}>
            <div style={{ 
              ...EMZI_TYPOGRAPHY.h2,
              fontSize: '14px', // Reduced from 16px
              fontWeight: 700,
              color: EMZI_COLORS.textPrimary,
              marginBottom: '4px' // Reduced from EMZI_SPACING.xs (6px)
            }}>
              {improvementFeedback.overallFeedback.title}
            </div>
            <div style={{ 
              ...EMZI_TYPOGRAPHY.body,
              fontSize: '13px', // Reduced from 14px
              color: EMZI_COLORS.textSecondary,
              lineHeight: '1.4' // Tighter line height
            }}>
              {improvementFeedback.overallFeedback.message}
            </div>
          </div>
          
          {/* Stats Row - Compact with 4 metrics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            marginTop: '10px',
            padding: '8px',
            background: EMZI_COLORS.surface,
            borderRadius: '6px',
            gap: '8px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '20px',
                fontWeight: 700,
                color: EMZI_COLORS.primary
              }}>
                {improvementFeedback.questionCount}/5
              </div>
              <div style={{ 
                ...EMZI_TYPOGRAPHY.caption,
                fontSize: '10px',
                color: EMZI_COLORS.textMuted,
                marginTop: '3px'
              }}>
                {language === 'bm' ? 'Soalan' : 'Questions'}
              </div>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '20px',
                fontWeight: 700,
                color: tier.color
              }}>
                {improvementFeedback.avgScore.toFixed(1)}
              </div>
              <div style={{ 
                ...EMZI_TYPOGRAPHY.caption,
                fontSize: '10px',
                color: EMZI_COLORS.textMuted,
                marginTop: '3px'
              }}>
                {language === 'bm' ? 'Purata' : 'Average'}
              </div>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '20px',
                fontWeight: 700,
                color: improvementFeedback.improvements.length === 0 
                  ? EMZI_COLORS.success 
                  : EMZI_COLORS.warning
              }}>
                {5 - improvementFeedback.improvements.length}
              </div>
              <div style={{ 
                ...EMZI_TYPOGRAPHY.caption,
                fontSize: '10px',
                color: EMZI_COLORS.textMuted,
                marginTop: '3px'
              }}>
                {language === 'bm' ? 'Kukuh' : 'Strong'}
              </div>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '20px',
                fontWeight: 700,
                color: improvementFeedback.aiAssistedCount > 0 ? EMZI_COLORS.accent : EMZI_COLORS.success
              }}>
                {improvementFeedback.aiAssistedCount}
              </div>
              <div style={{ 
                ...EMZI_TYPOGRAPHY.caption,
                fontSize: '10px',
                color: EMZI_COLORS.textMuted,
                marginTop: '3px'
              }}>
                {language === 'bm' ? 'AI Bantu' : 'AI Helped'}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* AI Rescue Explanation Banner (if any AI Rescue was used) */}
      {Object.values(answers || {}).some(a => a?.aiRescueUsed) && (
        <div style={{
          background: 'linear-gradient(135deg, #E0D4F7 0%, #F3E8FF 100%)',
          border: `2px solid ${EMZI_COLORS.accent}`,
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '12px',
          boxShadow: EMZI_SHADOWS.sm
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px'
          }}>
            <div style={{ fontSize: '20px', lineHeight: 1 }}>✨</div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 700,
                color: '#6B21A8',
                marginBottom: '6px'
              }}>
                {language === 'bm' ? 'Tentang AI Rescue' : 'About AI Rescue'}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#7C3AED',
                lineHeight: 1.5
              }}>
                {language === 'bm' 
                  ? 'Jawapan bertanda ✨ adalah AI Rescue - AI membantu anda selepas 3 cubaan. Walaupun jawapan betul, Brain Points dikurangkan 30% untuk menggambarkan pembelajaran dengan bantuan AI.'
                  : 'Answers marked with ✨ are AI Rescue - AI helped you after 3 attempts. While the answer is correct, Brain Points are reduced by 30% to reflect learning with AI assistance.'}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Improvement Areas - Compact */}
      {improvementFeedback.improvements.length > 0 && (
        <div style={{
          background: '#FEF3C7',
          border: `2px solid ${EMZI_COLORS.warning}`,
          borderRadius: '6px', // Reduced from EMZI_RADIUS.md (8px)
          padding: '10px', // Reduced from EMZI_SPACING.md (12px)
          marginBottom: '12px', // Reduced from EMZI_SPACING.lg (16px)
          boxShadow: EMZI_SHADOWS.sm
        }}>
          <h3 style={{
            ...EMZI_TYPOGRAPHY.h2,
            fontSize: '14px', // Reduced from 16px
            fontWeight: 700,
            color: '#92400E',
            marginBottom: '8px', // Reduced from EMZI_SPACING.sm (8px)
            display: 'flex',
            alignItems: 'center',
            gap: '4px' // Reduced from EMZI_SPACING.xs (6px)
          }}>
            💡 {language === 'bm' ? 'Area Pembelajaran' : 'Learning Insights'}
          </h3>
          <div style={{ ...EMZI_TYPOGRAPHY.body, fontSize: '13px', color: '#78350F' }}>
            {improvementFeedback.improvements.map((item, idx) => (
              <div key={`item-${idx}`} style={{
                background: '#FFFBEB',
                padding: '8px 10px', // Reduced from EMZI_SPACING.md (12px)
                borderRadius: '4px', // Reduced from EMZI_RADIUS.sm (6px)
                marginBottom: '6px', // Reduced from EMZI_SPACING.sm (8px)
                border: '1px solid #FDE68A'
              }}>
                <div style={{ 
                  fontWeight: 700,
                  marginBottom: '4px', // Reduced from EMZI_SPACING.xs (6px)
                  color: '#92400E',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px', // Reduced from EMZI_SPACING.xs (6px)
                  fontSize: '13px' // Added
                }}>
                  <span style={{
                    background: EMZI_COLORS.warning,
                    color: '#fff',
                    padding: '2px 6px', // Reduced from 2px 8px
                    borderRadius: '4px', // Reduced from EMZI_RADIUS.sm (6px)
                    fontSize: '11px', // Reduced from 12px
                    fontWeight: 600
                  }}>
                    Q{item.qIndex + 1}
                  </span>
                  {language === 'bm' ? 'Fokus untuk diperbaiki:' : 'Focus Area:'}
                </div>
                <div style={{ 
                  lineHeight: 1.6,
                  color: '#78350F'
                }}>
                  {item.suggestion}
                </div>
              </div>
            ))}
            <div style={{
              marginTop: EMZI_SPACING.md,
              padding: EMZI_SPACING.sm,
              background: '#FFFBEB',
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '13px',
              color: '#92400E',
              fontStyle: 'italic',
              textAlign: 'center'
            }}>
              {language === 'bm' 
                ? '📚 Gunakan insights ini untuk improve content creation anda pada masa hadapan'
                : '📚 Use these insights to improve your content creation in future sessions'}
            </div>
          </div>
        </div>
      )}
      
      {/* All Answers List - Enhanced "Learning Reflection" */}
      <div style={{ marginBottom: '80px' }}>
        <h3 style={{
          ...EMZI_TYPOGRAPHY.sectionTitle,
          fontSize: '16px',
          color: EMZI_COLORS.textPrimary,
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 700
        }}>
          📝 {language === 'bm' ? 'Jawapan Final Anda' : 'Your Final Answers'}
        </h3>
        
        {blueprint.map((question, idx) => {
          const answer = answers[idx];
          if (!answer) return null;
          
          const score = answer.bestScore || 0;
          // Calculate Brain Points with AI Rescue penalty
          const basePoints = Math.round(score * 10);
          const actualPoints = answer.aiRescueUsed ? Math.round(basePoints * 0.7) : basePoints;
          const penaltyApplied = answer.aiRescueUsed ? Math.round(basePoints * 0.3) : 0;
          
          const scoreColor = getScoreColor(score);
          const isStrong = score >= 8.0;
          
          return (
            <div
              key={`item-${idx}`}
              style={{
                background: answer.aiRescueUsed 
                  ? 'linear-gradient(135deg, #FFFFFF 0%, #F3E8FF 100%)'
                  : EMZI_COLORS.background,
                border: `2px solid ${answer.aiRescueUsed ? EMZI_COLORS.accent : isStrong ? EMZI_COLORS.success : EMZI_COLORS.border}`,
                borderRadius: '10px',
                padding: '14px',
                marginBottom: '12px',
                boxShadow: answer.aiRescueUsed ? EMZI_SHADOWS.md : EMZI_SHADOWS.card,
                transition: 'all 0.3s ease',
                animation: `fadeInUp 0.4s ease ${idx * 0.1}s both`
              }}
            >
              <style>{`
                @keyframes fadeInUp {
                  from {
                    opacity: 0;
                    transform: translateY(20px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
              `}</style>
              
              {/* Question Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '10px',
                gap: '10px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    ...EMZI_TYPOGRAPHY.label,
                    fontSize: '10px',
                    color: EMZI_COLORS.textMuted,
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 700
                  }}>
                    {language === 'bm' ? 'SOALAN' : 'QUESTION'} {idx + 1}/5
                  </div>
                  <div style={{
                    ...EMZI_TYPOGRAPHY.body,
                    fontSize: '15px',
                    fontWeight: 600,
                    color: EMZI_COLORS.textPrimary,
                    marginBottom: '6px',
                    lineHeight: 1.4
                  }}>
                    {question.q_en || question.q_bm || question.q}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: EMZI_COLORS.textMuted,
                    fontStyle: 'italic'
                  }}>
                    🎯 {language === 'bm' ? 'Fokus' : 'Focus'}: {question.focus?.join(', ') || 'N/A'}
                  </div>
                </div>
                
                {/* Score Badge */}
                <div style={{
                  background: scoreColor,
                  color: EMZI_COLORS.textWhite,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'center',
                  minWidth: '70px',
                  flexShrink: 0,
                  boxShadow: EMZI_SHADOWS.sm
                }}>
                  {score.toFixed(1)}/10
                  <div style={{ 
                    fontSize: '10px',
                    fontWeight: 400,
                    opacity: 0.95,
                    marginTop: '2px'
                  }}>
                    ⭐ Score
                  </div>
                </div>
              </div>
              
              {/* AI Rescue Badge (if used) */}
              {answer.aiRescueUsed && (
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 700,
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    ✨ {language === 'bm' ? 'Jawapan AI Rescue' : 'AI Rescue Answer'}
                  </span>
                  <span style={{
                    background: 'rgba(255,255,255,0.25)',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600
                  }}>
                    -30% BP
                  </span>
                </div>
              )}
              
              {/* Answer Text */}
              <div style={{
                background: answer.aiRescueUsed ? 'rgba(255,255,255,0.8)' : EMZI_COLORS.surface,
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '10px',
                ...EMZI_TYPOGRAPHY.body,
                fontSize: '14px',
                color: EMZI_COLORS.textPrimary,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                border: `1px solid ${answer.aiRescueUsed ? '#E9D5FF' : EMZI_COLORS.borderLight}`,
                minHeight: '60px'
              }}>
                {answer.text || (language === 'bm' ? 'Tiada jawapan' : 'No answer provided')}
              </div>
              
              {/* Brain Points Breakdown */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 12px',
                background: answer.aiRescueUsed 
                  ? 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)'
                  : EMZI_COLORS.surface,
                borderRadius: '6px',
                gap: '12px',
                border: `1px solid ${answer.aiRescueUsed ? EMZI_COLORS.warning : EMZI_COLORS.borderLight}`
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    ...EMZI_TYPOGRAPHY.caption,
                    color: EMZI_COLORS.textMuted,
                    marginBottom: '3px',
                    fontSize: '11px'
                  }}>
                    🧠 {language === 'bm' ? 'Brain Points Diperoleh' : 'Brain Points Earned'}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '6px'
                  }}>
                    <span style={{
                      fontSize: '20px',
                      fontWeight: 700,
                      color: answer.aiRescueUsed ? EMZI_COLORS.warning : EMZI_COLORS.success
                    }}>
                      {actualPoints}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: EMZI_COLORS.textSecondary
                    }}>
                      / 100 {language === 'bm' ? 'poin' : 'points'}
                    </span>
                    {answer.aiRescueUsed && (
                      <span style={{
                        fontSize: '11px',
                        color: EMZI_COLORS.error,
                        fontWeight: 600,
                        marginLeft: '4px'
                      }}>
                        (-{penaltyApplied} {language === 'bm' ? 'penalti' : 'penalty'})
                      </span>
                    )}
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '11px',
                  color: EMZI_COLORS.textSecondary
                }}>
                  <span>
                    {answer.attempts?.length || 1} {language === 'bm' ? 'cubaan' : answer.attempts?.length === 1 ? 'attempt' : 'attempts'}
                  </span>
                  {score >= 9.0 && (
                    <span style={{ 
                      color: EMZI_COLORS.success,
                      fontWeight: 700,
                      background: '#D1FAE5',
                      padding: '3px 8px',
                      borderRadius: '4px'
                    }}>
                      ⭐ {language === 'bm' ? 'Cemerlang' : 'Excellent'}
                    </span>
                  )}
                  {score < 8.0 && score >= 6.0 && (
                    <span style={{ 
                      color: EMZI_COLORS.warning,
                      fontWeight: 600,
                      background: '#FEF3C7',
                      padding: '3px 8px',
                      borderRadius: '4px'
                    }}>
                      💡 {language === 'bm' ? 'Boleh improve' : 'Can improve'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Navigation Buttons - Inline (Below Last Answer) */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px 0',
        paddingBottom: `calc(20px + env(safe-area-inset-bottom, 20px))` // Extra padding for mobile safe area
      }}>
        <div style={{
          display: 'flex',
          gap: EMZI_SPACING.md,
          alignItems: 'center'
        }}>
          {/* Back to SOA Drill Button - EMZI Outlined Style */}
          <button
            onClick={onBack}
            style={{
              ...EMZI_BUTTON_STYLES.outlined,
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: EMZI_SPACING.xs,
              minHeight: '48px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = EMZI_COLORS.surface;
              e.currentTarget.style.borderColor = EMZI_COLORS.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = EMZI_COLORS.background;
              e.currentTarget.style.borderColor = EMZI_COLORS.border;
            }}
          >
            <span style={{ fontSize: '16px' }}>←</span>
            <span>{language === 'bm' ? 'Kembali ke SOA Drill' : 'Back to SOA Drill'}</span>
          </button>
          
          {/* Continue to Script Button - EMZI Outlined Style (Matching Back Button) */}
          <button
            onClick={onComplete}
            style={{
              ...EMZI_BUTTON_STYLES.outlined,
              flex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: EMZI_SPACING.xs,
              minHeight: '48px',
              fontSize: '15px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = EMZI_COLORS.surface;
              e.currentTarget.style.borderColor = EMZI_COLORS.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = EMZI_COLORS.background;
              e.currentTarget.style.borderColor = EMZI_COLORS.border;
            }}
          >
            <span>{language === 'bm' ? 'Teruskan ke Script' : 'Continue to Script'}</span>
            <span style={{ fontSize: '16px' }}>→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step7_5_SOAReview;
